/**
 * ServiceOrderDialog - Authorization Dialog Component
 * Extracted from ClientDetailPage to reduce file size
 * Full-featured version with caregiver assignment and payroll
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClipboardList, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const PAYER_TYPE_OPTIONS = [
  { value: "medicaid", label: "Medicaid" },
  { value: "medicare", label: "Medicare" },
  { value: "private_insurance", label: "Private Insurance" },
  { value: "private_pay", label: "Private Pay" },
  { value: "other", label: "Other" },
];

const SERVICE_TYPE_OPTIONS = [
  { value: "PCA", label: "Personal Care Aide (PCA)" },
  { value: "HHA", label: "Home Health Aide (HHA)" },
  { value: "CDPAP", label: "CDPAP" },
  { value: "Skilled_Nursing", label: "Skilled Nursing" },
  { value: "Physical_Therapy", label: "Physical Therapy" },
  { value: "Other", label: "Other" },
];

const PAYROLL_ITEM_OPTIONS = [
  { value: "hourly_personal_care", label: "Hourly Personal Care" },
  { value: "hourly_travel_time", label: "Hourly Travel Time" },
  { value: "hourly_supportive_care", label: "Hourly Supportive Care" },
];

export function ServiceOrderDialog({
  open,
  onOpenChange,
  clientName,
  editingServiceOrderId,
  newServiceOrder,
  setNewServiceOrder,
  masterServiceCodes,
  employees,
  saving,
  onSave,
  onResetForm,
  calculateServiceOrderTotals,
  handleServiceCodeChange,
}) {
  // Reset form when dialog closes
  const handleOpenChange = (isOpen) => {
    if (!isOpen && onResetForm) {
      onResetForm();
    }
    onOpenChange(isOpen);
  };

  const toggleDayOfWeek = (day) => {
    setNewServiceOrder((prev) => ({
      ...prev,
      days_of_week: prev.days_of_week?.includes(day)
        ? prev.days_of_week.filter((d) => d !== day)
        : [...(prev.days_of_week || []), day],
    }));
  };

  // Handle caregiver change with payroll rate lookup
  const handleCaregiverChange = (value) => {
    const emp = employees?.find(e => e.id === value);
    
    // Find matching payroll rate from employee's payroll_items
    let empPayrollRate = "";
    let hasPayrollRate = false;
    
    if (emp?.payroll_items && newServiceOrder.payroll_item) {
      const matchingItem = emp.payroll_items.find(
        item => item.item_name === newServiceOrder.payroll_item
      );
      if (matchingItem?.rate) {
        empPayrollRate = matchingItem.rate.toString();
        hasPayrollRate = true;
      }
    }
    
    // Fallback to general pay_rate if no specific payroll item
    if (!hasPayrollRate && emp?.pay_rate) {
      empPayrollRate = emp.pay_rate.toString();
      hasPayrollRate = true;
    }
    
    // Show alert if employee has no payroll rate configured
    if (!hasPayrollRate && emp) {
      toast.warning(
        `${emp.name} does not have a payroll rate configured. Please add a payroll rate in their employee profile.`,
        { duration: 5000 }
      );
    }
    
    setNewServiceOrder(prev => ({
      ...prev,
      caregiver_id: value,
      caregiver_name: emp?.name || "",
      payroll_rate: empPayrollRate || prev.payroll_rate,
      _caregiverHasPayrollRate: hasPayrollRate
    }));
  };

  // Handle payroll item change
  const handlePayrollItemChange = (value) => {
    const selectedEmp = employees?.find(e => e.id === newServiceOrder.caregiver_id);
    let newPayrollRate = newServiceOrder.payroll_rate;
    let hasPayrollRate = newServiceOrder._caregiverHasPayrollRate;
    
    if (selectedEmp) {
      const matchingItem = selectedEmp.payroll_items?.find(
        item => item.item_name === value
      );
      if (matchingItem?.rate) {
        newPayrollRate = matchingItem.rate.toString();
        hasPayrollRate = true;
      } else if (selectedEmp.pay_rate) {
        newPayrollRate = selectedEmp.pay_rate.toString();
        hasPayrollRate = true;
      } else {
        hasPayrollRate = false;
      }
    }
    
    setNewServiceOrder(prev => ({ 
      ...prev, 
      payroll_item: value,
      payroll_rate: newPayrollRate,
      _caregiverHasPayrollRate: hasPayrollRate
    }));
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-teal-600" />
            {editingServiceOrderId ? "Edit Authorization" : "Add Authorization"}
          </DialogTitle>
          <DialogDescription>
            Authorization for {clientName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Service & Basic Info */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Service Code</Label>
              <Select
                value={newServiceOrder.service_code || ""}
                onValueChange={handleServiceCodeChange}
              >
                <SelectTrigger data-testid="service-code-select">
                  <SelectValue placeholder="Select service code" />
                </SelectTrigger>
                <SelectContent>
                  {masterServiceCodes?.length > 0 ? (
                    masterServiceCodes.map((code) => (
                      <SelectItem key={code.id} value={code.code}>
                        {code.code} - {code.description}
                      </SelectItem>
                    ))
                  ) : (
                    <>
                      <SelectItem value="T1019">T1019 - Personal Care</SelectItem>
                      <SelectItem value="S5125">S5125 - Supportive Care</SelectItem>
                      <SelectItem value="T1019-U3">T1019 Modifier U3 - Travel Time</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
              {(!masterServiceCodes || masterServiceCodes.length === 0) && (
                <p className="text-xs text-amber-600">
                  Configure service codes in Settings → Service Codes
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Units/Hour</Label>
              <Input
                type="number"
                step="0.25"
                value={newServiceOrder.units_per_hour || ""}
                onChange={(e) =>
                  setNewServiceOrder((prev) =>
                    calculateServiceOrderTotals("units_per_hour", e.target.value, prev)
                  )
                }
                placeholder="1"
                data-testid="units-per-hour-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Cost Per Hour</Label>
              <Input
                type="number"
                step="0.01"
                value={newServiceOrder.cost_per_hour || ""}
                onChange={(e) =>
                  setNewServiceOrder((prev) =>
                    calculateServiceOrderTotals("cost_per_hour", e.target.value, prev)
                  )
                }
                placeholder="0.00"
                data-testid="cost-per-hour-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Patient Number</Label>
              <Input
                value={newServiceOrder.patient_number || ""}
                onChange={(e) =>
                  setNewServiceOrder((prev) => ({
                    ...prev,
                    patient_number: e.target.value,
                  }))
                }
                placeholder="Patient #"
                data-testid="patient-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Payor *</Label>
              <Select
                value={newServiceOrder.payer_type || "medicaid"}
                onValueChange={(value) =>
                  setNewServiceOrder((prev) => ({ ...prev, payer_type: value }))
                }
              >
                <SelectTrigger data-testid="payer-type-select">
                  <SelectValue placeholder="Select payer" />
                </SelectTrigger>
                <SelectContent>
                  {PAYER_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={newServiceOrder.status || "active"}
                onValueChange={(value) =>
                  setNewServiceOrder((prev) => ({ ...prev, status: value }))
                }
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Authorization Info */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-slate-700 mb-3">Authorization Details</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Authorization Number *</Label>
                <Input
                  value={newServiceOrder.authorization_number || ""}
                  onChange={(e) =>
                    setNewServiceOrder((prev) => ({
                      ...prev,
                      authorization_number: e.target.value,
                    }))
                  }
                  placeholder="Auth #"
                  data-testid="auth-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Service Type</Label>
                <Select
                  value={newServiceOrder.service_type || "PCA"}
                  onValueChange={(value) =>
                    setNewServiceOrder((prev) => ({ ...prev, service_type: value }))
                  }
                >
                  <SelectTrigger data-testid="service-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Total Units Approved</Label>
                <Input
                  type="number"
                  value={newServiceOrder.units_approved || ""}
                  onChange={(e) =>
                    setNewServiceOrder((prev) => ({
                      ...prev,
                      units_approved: e.target.value,
                    }))
                  }
                  placeholder="Units"
                  data-testid="units-approved-input"
                />
              </div>
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={newServiceOrder.start_date || ""}
                onChange={(e) =>
                  setNewServiceOrder((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                  }))
                }
                data-testid="start-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>End Date *</Label>
              <Input
                type="date"
                value={newServiceOrder.end_date || ""}
                onChange={(e) =>
                  setNewServiceOrder((prev) => ({
                    ...prev,
                    end_date: e.target.value,
                  }))
                }
                data-testid="end-date-input"
              />
            </div>
          </div>

          {/* Schedule */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-slate-700 mb-3">Schedule</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hours Per Week</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={newServiceOrder.hours_per_week || ""}
                  onChange={(e) =>
                    setNewServiceOrder((prev) =>
                      calculateServiceOrderTotals("hours_per_week", e.target.value, prev)
                    )
                  }
                  placeholder="0"
                  data-testid="hours-per-week-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Hours Per Day</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={newServiceOrder.hours_per_day || ""}
                  onChange={(e) =>
                    setNewServiceOrder((prev) => ({
                      ...prev,
                      hours_per_day: e.target.value,
                    }))
                  }
                  placeholder="0"
                  data-testid="hours-per-day-input"
                />
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <Label>Days of Week</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <div
                    key={day}
                    className={`px-3 py-1.5 rounded-full cursor-pointer text-sm font-medium transition-colors ${
                      newServiceOrder.days_of_week?.includes(day)
                        ? "bg-teal-600 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                    onClick={() => toggleDayOfWeek(day)}
                    data-testid={`day-${day.toLowerCase()}`}
                  >
                    {day}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="time"
                  value={newServiceOrder.start_time || ""}
                  onChange={(e) =>
                    setNewServiceOrder((prev) => ({
                      ...prev,
                      start_time: e.target.value,
                    }))
                  }
                  data-testid="start-time-input"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <Input
                  type="time"
                  value={newServiceOrder.end_time || ""}
                  onChange={(e) =>
                    setNewServiceOrder((prev) => ({
                      ...prev,
                      end_time: e.target.value,
                    }))
                  }
                  data-testid="end-time-input"
                />
              </div>
            </div>
          </div>

          {/* Caregiver Assignment */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-slate-700 mb-3">Caregiver Assignment & Payroll</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-1">
                  Assigned Caregiver
                  {newServiceOrder.caregiver_id && <span className="text-emerald-600 text-[10px]">(assigned)</span>}
                </Label>
                <Select
                  value={newServiceOrder.caregiver_id || ""}
                  onValueChange={handleCaregiverChange}
                >
                  <SelectTrigger data-testid="caregiver-select">
                    <SelectValue placeholder="Select caregiver" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees?.map(emp => {
                      const matchingPayrollItem = emp.payroll_items?.find(
                        item => item.item_name === newServiceOrder.payroll_item
                      );
                      const displayRate = matchingPayrollItem?.rate || emp.pay_rate;
                      return (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name} {displayRate ? `($${displayRate}/hr)` : '(no rate)'}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {newServiceOrder.caregiver_id && newServiceOrder._caregiverHasPayrollRate === false && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    No payroll rate configured. Add rate in Employees → {newServiceOrder.caregiver_name} → Payroll
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-1">
                  Payroll Item
                  {newServiceOrder.payroll_item && <span className="text-emerald-600 text-[10px]">(auto)</span>}
                </Label>
                <Select
                  value={newServiceOrder.payroll_item || ""}
                  onValueChange={handlePayrollItemChange}
                >
                  <SelectTrigger 
                    data-testid="payroll-item-select"
                    className={newServiceOrder.payroll_item ? "bg-emerald-50 border-emerald-200" : ""}
                  >
                    <SelectValue placeholder="Select payroll item" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYROLL_ITEM_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Billing Rates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Billing Rate ($/hour)
                {newServiceOrder.billing_rate && newServiceOrder.service_code && <span className="text-emerald-600 text-[10px]">(auto)</span>}
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <Input
                  type="number"
                  step="0.01"
                  value={newServiceOrder.billing_rate || ""}
                  onChange={(e) => setNewServiceOrder(prev => ({ ...prev, billing_rate: e.target.value }))}
                  placeholder="Reimbursement rate"
                  className={`pl-7 ${newServiceOrder.billing_rate && newServiceOrder.service_code ? "bg-emerald-50 border-emerald-200" : ""}`}
                  data-testid="billing-rate-input"
                />
              </div>
              <p className="text-xs text-slate-400">Amount billed to payer</p>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Payroll Rate ($/hour)
                {newServiceOrder.payroll_rate && (newServiceOrder.service_code || newServiceOrder.caregiver_id) && <span className="text-emerald-600 text-[10px]">(auto)</span>}
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <Input
                  type="number"
                  step="0.01"
                  value={newServiceOrder.payroll_rate || ""}
                  onChange={(e) => setNewServiceOrder(prev => ({ ...prev, payroll_rate: e.target.value }))}
                  placeholder="Pay rate"
                  className={`pl-7 ${newServiceOrder.payroll_rate && (newServiceOrder.service_code || newServiceOrder.caregiver_id) ? "bg-emerald-50 border-emerald-200" : ""}`}
                  data-testid="payroll-rate-input"
                />
              </div>
              <p className="text-xs text-slate-400">Amount paid to caregiver</p>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={newServiceOrder.notes || ""}
              onChange={(e) =>
                setNewServiceOrder((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder="Any additional notes about this service order..."
              rows={3}
              data-testid="service-order-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={onSave}
            disabled={saving}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="save-authorization-btn"
          >
            {saving ? "Saving..." : "Save Authorization"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
